import { useRef, useEffect, useState, useCallback, PropsWithChildren } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { Button, Heading, Icon } from '@alto-avios/alto-ui'
import { deviceSizes } from '../../theme/deviceSizes'
import { MobileModalProps } from './MobileModal.types'
import { useLiftMediaQuery } from '../../hooks/useLiftMediaQuery'
import styles from './MobileModal.module.scss'

// Implementation based on Next.js documentation https://github.com/vercel/next.js/tree/canary/examples/with-portals.

type ClientOnlyPortalProps = {
  selector: string
  children?: any
}

function ClientOnlyPortal({ children, selector }: PropsWithChildren<ClientOnlyPortalProps>) {
  const ref = useRef<Element | null>(null)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    ref.current = document.querySelector(selector)
    setMounted(true)
  }, [selector])

  return mounted && ref.current ? createPortal(children, ref.current) : null
}

function MobileModal({
  id,
  heading,
  children,
  isModalVisible,
  handleModalClose,
  disablePlaceholder,
  allowScroll,
  placeholder,
}: MobileModalProps): JSX.Element {
  const isTabletOrSmaller = useLiftMediaQuery(`(max-width: ${deviceSizes.medium})`)

  useEffect(() => {
    const originalOverflow = document.body.style.overflow ?? ''
    if (isModalVisible && !allowScroll) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = originalOverflow
    }
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isModalVisible, allowScroll])

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleModalClose()
      }
    },
    [handleModalClose]
  )

  useEffect(() => {
    if (isModalVisible) {
      window.addEventListener('keyup', handleKeyUp)

      return () => {
        window.removeEventListener('keyup', handleKeyUp)
      }
    }

    return undefined
  }, [isModalVisible, handleKeyUp])

  if (!isTabletOrSmaller || !isModalVisible) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{placeholder || children}</>
  }

  return (
    <>
      {!disablePlaceholder && (placeholder || children)}
      <ClientOnlyPortal selector="#modal">
        <section
          id={id}
          data-testid="mobile-modal"
          className={classNames(styles.mobileModal, 'site-MobileModal')}
          role="dialog"
        >
          <Button aria-label="Close" styleVariant="white" onPress={handleModalClose}>
            <Icon iconName="xmark" iconSize="2x" />
          </Button>
          {heading && (
            <Heading as="h4" className={styles.heading}>
              {heading}
            </Heading>
          )}
          {children}
        </section>
      </ClientOnlyPortal>
    </>
  )
}

export default MobileModal
